import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle2, XCircle, PartyPopper, Frown, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public page hit when an applicant clicks Accept / Decline in their offer email.
 * URL format: /careers/offer/respond?token=<uuid_hex>&action=accept|reject
 *
 * Flow:
 *   1. On load, GET /careers/offer/status?token=... to display applicant name + role.
 *   2. Show confirm button (so a preview-fetch by mail clients can't auto-record).
 *   3. On confirm, POST /careers/offer/respond and show final confirmation.
 */
const OfferResponsePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const action = params.get('action'); // "accept" | "reject"

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token || !['accept', 'reject'].includes(action)) {
      setError('This link is missing required information. Please open the link from your email again.');
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/careers/offer/status?token=${encodeURIComponent(token)}`)
      .then((r) => setStatus(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'This offer link is invalid or has been revoked.'))
      .finally(() => setLoading(false));
  }, [token, action]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await axios.post(`${API}/careers/offer/respond`, { token, action, note: note || undefined });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not record your response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- render states ----------------
  if (loading) {
    return (
      <Shell>
        <div className="py-24 flex justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
        </div>
      </Shell>
    );
  }

  if (error && !status) {
    return (
      <Shell>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center space-y-3" data-testid="offer-error">
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h1 className="text-xl font-bold text-red-900">This link is not valid</h1>
            <p className="text-sm text-red-800">{error}</p>
            <Link to="/">
              <Button variant="outline" className="mt-4" data-testid="offer-home-btn">
                Return to Zazu
              </Button>
            </Link>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // Already responded — show a recap regardless of what button they clicked this time
  if (status?.already_responded || result) {
    const finalAction = result?.action || status?.response;
    const isAccept = finalAction === 'accept';
    return (
      <Shell>
        <Card className={isAccept ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 bg-slate-50'}>
          <CardContent className="p-8 text-center space-y-4" data-testid="offer-final">
            {isAccept ? (
              <PartyPopper className="w-16 h-16 text-emerald-500 mx-auto" />
            ) : (
              <Frown className="w-16 h-16 text-slate-500 mx-auto" />
            )}
            <h1 className="text-2xl font-black text-slate-900">
              {isAccept ? 'Welcome to Zazu! 🎉' : 'Thanks for letting us know'}
            </h1>
            <p className="text-slate-700">
              {isAccept
                ? `Your acceptance of the ${status?.role_label || 'internship'} offer has been recorded. Your Zazu staff account is provisioned, comped Premium is active, and your Day-1 task list is waiting. Check your inbox for a welcome email with login instructions.`
                : `We've recorded your decision to decline. We wish you the very best, ${status?.applicant_first_name || 'friend'} — and we'd love to stay in touch as your journey evolves.`}
            </p>
            {status?.response_at && (
              <p className="text-xs text-slate-500">Recorded {new Date(status.response_at).toLocaleString()}</p>
            )}
            <Link to="/">
              <Button className={isAccept ? 'bg-emerald-500 hover:bg-emerald-600 mt-2' : 'mt-2'} data-testid="offer-home-btn">
                Visit Zazu <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  const isAccept = action === 'accept';
  return (
    <Shell>
      <Card className={isAccept ? 'border-emerald-300' : 'border-red-300'}>
        <CardContent className="p-8 space-y-5" data-testid="offer-confirm">
          <div className="text-center space-y-2">
            {isAccept ? (
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            ) : (
              <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            )}
            <h1 className="text-2xl font-black text-slate-900">
              {isAccept ? 'Accept your Zazu offer?' : 'Decline your Zazu offer?'}
            </h1>
            <p className="text-slate-600 text-sm">
              Hi <strong>{status?.applicant_first_name}</strong> — you&apos;re about to{' '}
              <strong>{isAccept ? 'accept' : 'decline'}</strong> the{' '}
              <strong>{status?.role_label}</strong> offer
              {status?.start_date ? <> starting <strong>{status.start_date}</strong></> : null}.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600">
              {isAccept ? 'Anything you want the team to know? (optional)' : 'Would you like to share a brief reason? (optional)'}
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder={isAccept ? "I'm excited to start! One question…" : "Timing didn't work, exploring another opportunity, etc."}
              rows={3}
              data-testid="offer-note-input"
            />
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Link to="/">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="offer-cancel-btn">
                Cancel
              </Button>
            </Link>
            <Button
              onClick={submit}
              disabled={submitting}
              className={
                isAccept
                  ? 'bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto'
                  : 'bg-red-500 hover:bg-red-600 w-full sm:w-auto'
              }
              data-testid="offer-confirm-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isAccept ? (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {isAccept ? 'Confirm acceptance' : 'Confirm decline'}
            </Button>
          </div>

          <p className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
            This link is unique to you and can only be used once. Questions? Reply to the offer email.
          </p>
        </CardContent>
      </Card>
    </Shell>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center px-4 py-12">
    <div className="max-w-lg w-full">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-black text-emerald-600 tracking-tight">Zazu</h2>
        <p className="text-xs text-slate-500 uppercase tracking-widest">Kids Learning Platform</p>
      </div>
      {children}
    </div>
  </div>
);

export default OfferResponsePage;
